export interface DiagnosticQuestion {
  id: number;
  title: string;
  question: string;
}

export const diagnosticQuestions: DiagnosticQuestion[] = [
  {
    id: 1,
    title: "Trayectoria e Interés",
    question: "Cuéntame sobre tu trayectoria en relación con la inteligencia artificial. ¿Qué despertó tu interés en este campo y qué es lo que más te entusiasma de su potencial?"
  },
  {
    id: 2,
    title: "Comprensión Conceptual",
    question: "En tus propias palabras, ¿cuál es el valor principal que la inteligencia artificial puede aportar a tu rol actual, a tu organización o a tu práctica profesional?"
  },
  {
    id: 3,
    title: "Uso Práctico Cotidiano",
    question: "¿Cómo utilizas herramientas de IA generativa como ChatGPT, Gemini o Copilot en tu día a día? Describe qué tipo de tareas o procesos has comenzado a explorar o mejorar con ellas."
  },
  {
    id: 4,
    title: "Proyecto de Impacto",
    question: "Describe el proyecto de IA más exitoso o desafiante que hayas completado. Explica el problema (de negocio, profesional o personal) que resolviste, el proceso que seguiste y qué factores clave hicieron que el resultado fuera exitoso."
  },
  {
    id: 5,
    title: "Gestión de Datos",
    question: "En ese proyecto, ¿qué proceso específico seguiste para asegurar la calidad de la información o los datos? Describe los pasos que tomaste para manejar datos faltantes, inconsistencias o duplicados."
  },
  {
    id: 6,
    title: "Aprendizaje Crítico",
    question: "Describe una situación en la que la solución de IA que implementaste produjo un resultado sorprendente o inesperado. ¿Qué aprendiste de esa experiencia sobre las limitaciones o el potencial oculto de la herramienta?"
  },
  {
    id: 7,
    title: "Arquitectura de Agentes",
    question: "¿En qué escenario de negocio elegirías implementar un sistema de agentes de IA autónomos en lugar de una automatización de flujo de trabajo tradicional? ¿Cómo diseñarías el mecanismo de supervisión humana para garantizar que el sistema se mantenga alineado con los objetivos?"
  },
  {
    id: 8,
    title: "Integración de Ecosistemas",
    question: "Imagina que necesitas integrar múltiples herramientas de IA para un proceso de negocio o flujo profesional (ej. un agente que analiza datos y genera contenido). ¿Cómo abordarías esta integración para asegurar que los sistemas funcionen de manera coordinada y logren resultados medibles?"
  },
  {
    id: 9,
    title: "Interpretabilidad del Sistema",
    question: "Al diseñar una solución de IA, ¿cómo equilibras la necesidad de crear un sistema complejo y potente con la importancia de que los líderes y usuarios de negocio puedan entender cómo toma sus decisiones?"
  },
  {
    id: 10,
    title: "Alineación de Negocio",
    question: "¿Cómo se alinea una iniciativa de IA que liderarías con tu modelo de negocio (o el de tu organización o clientes)? ¿Qué ventajas competitivas específicas buscarías construir que sean difíciles de replicar por la competencia?"
  },
  {
    id: 11,
    title: "Cuantificación del Valor (ROI)",
    question: "¿Cómo definirías y cuantificarías el valor estratégico de una nueva iniciativa de IA, más allá del ahorro de costos directo, para demostrar un retorno de inversión sostenible a los tomadores de decisión (alta dirección, clientes o tú mismo como profesional)?"
  },
  {
    id: 12,
    title: "Escalabilidad y Riesgo",
    question: "¿Cuáles consideras que son los riesgos clave (operativos, regulatorios, competitivos) de una implementación de IA a gran escala? ¿Qué factores determinarían si una iniciativa puede escalar exitosamente de un piloto a una solución productiva a gran escala?"
  },
  {
    id: 13,
    title: "Reinvención del Modelo",
    question: "Describe un ejemplo de cómo la IA te ha permitido crear un modelo de negocio nuevo o rediseñar fundamentalmente un proceso, rol o servicio (dentro de una organización o en tu práctica profesional). ¿Cuáles fueron los resultados tangibles de esta transformación?"
  },
  {
    id: 14,
    title: "Gobernanza Ética",
    question: "¿Cómo gestionas la tensión entre la velocidad de la innovación en IA y la necesidad de una gobernanza ética y responsable? Describe tu metodología para asegurar la transparencia en un sistema de decisiones automatizado."
  },
  {
    id: 15,
    title: "Liderazgo de Cambio",
    question: "¿Estás preparado para ser un referente y evangelizador de la transformación de IA en tu organización, equipo o comunidad profesional? Explica cómo fomentarías una cultura que adopte la IA, gestione el cambio de manera proactiva y alinee a las personas con las que trabajas."
  }
];

// sectorOptions and areaOptions removed — now loaded dynamically from DB tables industry_sectors and business_areas

export type DiagnosticLevel = 'iniciador' | 'explorador' | 'implementador' | 'integrador' | 'estratega' | 'transformador';

export interface DiagnosticAnswer {
  text: string;
  cannotAnswer: boolean;
}

export interface DiagnosticResult {
  level: DiagnosticLevel;
  levelNumber: number;
  title: string;
  description: string;
  answeredQuestions: number;
  totalQuestions: number;
  completionPercentage: number;
}

export const levelInfo: Record<DiagnosticLevel, { number: number; title: string; description: string; fullDescription: string; characteristics: string[] }> = {
  iniciador: {
    number: 0,
    title: 'Iniciador',
    description: 'Conciencia incipiente de la IA, sin conocimientos ni experiencia práctica.',
    fullDescription: 'El profesional en nivel Iniciador posee una conciencia incipiente de la inteligencia artificial. No cuenta con conocimientos técnicos ni experiencia práctica en el uso de herramientas de IA. Su principal desafío es superar el escepticismo o la indiferencia y avanzar hacia una alfabetización básica que le permita comprender qué es la IA y cómo puede ser relevante para su rol profesional. En este nivel, la interacción con sistemas inteligentes es nula o extremadamente superficial.',
    characteristics: [
      'Lenguaje: Usa términos genéricos o imprecisos como "robots", "automatización" o "inteligencia artificial" sin distinguir entre conceptos.',
      'Enfoque: No tiene un enfoque definido; la IA es un concepto abstracto sin conexión con su trabajo diario.',
      'Visión del Riesgo: Falta de conciencia sobre los riesgos, o los considera irrelevantes en su etapa actual.',
      'Medida del Éxito: El éxito se mide por la alfabetización básica: comprender qué es la IA y para qué puede servir.'
    ]
  },
  explorador: {
    number: 1,
    title: 'Explorador',
    description: 'Experimentación activa con herramientas de IA generativa para productividad personal.',
    fullDescription: 'El Explorador ha dado el paso de la curiosidad a la experimentación activa. Utiliza herramientas de IA generativa como ChatGPT, Gemini o Copilot de forma regular para mejorar su productividad personal: redacción de correos, resúmenes de documentos, generación de ideas y consultas rápidas. Comprende el concepto de prompt engineering a nivel básico y es capaz de obtener resultados útiles, aunque su uso sigue siendo individual y no sistematizado dentro de procesos de trabajo formales.',
    characteristics: [
      'Lenguaje: Usa nombres de herramientas específicas (ChatGPT, Copilot) y términos como "prompt", "modelo de lenguaje" con comprensión básica.',
      'Enfoque: Productividad personal — usar IA para hacer sus tareas individuales más rápido.',
      'Visión del Riesgo: Identifica riesgos básicos como respuestas incorrectas o "alucinaciones" del modelo.',
      'Medida del Éxito: Tareas completadas más rápido o con mejor calidad gracias a la asistencia de IA.'
    ]
  },
  implementador: {
    number: 2,
    title: 'Implementador',
    description: 'Utilidad aplicada: ha resuelto al menos un desafío profesional real con IA.',
    fullDescription: 'El Implementador ha trascendido la experimentación personal para lograr una utilidad aplicada. Ha identificado y resuelto al menos un desafío profesional concreto utilizando IA, demostrando capacidad para seleccionar la herramienta adecuada, preparar los datos necesarios y medir el resultado. Su enfoque está en la automatización de tareas y la optimización de flujos de trabajo específicos. Comprende conceptos como calidad de datos, limitaciones de los modelos y puede articular el valor que la IA aporta a su función.',
    characteristics: [
      'Lenguaje: Articula conceptos como "calidad de datos", "automatización de flujos", "limitaciones del modelo" con propiedad.',
      'Enfoque: Resolver problemas específicos — automatizar un proceso, mejorar la eficiencia de una tarea concreta.',
      'Visión del Riesgo: Identifica riesgos de fallo técnico: errores en datos, rendimiento deficiente del modelo, resultados inconsistentes.',
      'Medida del Éxito: Un proceso automatizado, un problema resuelto o una mejora medible en eficiencia.'
    ]
  },
  integrador: {
    number: 3,
    title: 'Integrador',
    description: 'Diseña sistemas complejos orquestando múltiples herramientas y agentes de IA.',
    fullDescription: 'El Integrador representa un salto cualitativo significativo. Este profesional no solo usa herramientas individuales, sino que diseña sistemas complejos que orquestan múltiples componentes de IA: agentes autónomos, sistemas RAG (Retrieval-Augmented Generation), automatizaciones multi-paso y pipelines de datos. Posee un conocimiento técnico profundo que le permite evaluar arquitecturas, diseñar mecanismos de supervisión humana y garantizar que los sistemas funcionen de manera coordinada. Su enfoque trasciende la tarea individual para abordar procesos completos.',
    characteristics: [
      'Lenguaje: Maneja terminología técnica avanzada: "RAG", "agentes autónomos", "orquestación", "pipelines", "fine-tuning".',
      'Enfoque: Diseñar e integrar sistemas — conectar múltiples herramientas de IA para resolver procesos completos.',
      'Visión del Riesgo: Comprende riesgos sistémicos: fallos en cadena, dependencias entre componentes, necesidad de supervisión humana.',
      'Medida del Éxito: Sistemas integrados funcionando de forma coordinada con resultados medibles a nivel de proceso.'
    ]
  },
  estratega: {
    number: 4,
    title: 'Estratega',
    description: 'Visión de valor empresarial: alinea iniciativas de IA con crecimiento y ROI comprobado.',
    fullDescription: 'El Estratega posee una visión de valor empresarial. Su enfoque ya no es la herramienta o el sistema técnico, sino cómo la IA se alinea con el modelo de negocio, genera ventajas competitivas y produce un retorno de inversión comprobado. Es capaz de cuantificar el valor estratégico de las iniciativas de IA más allá del ahorro de costos directo, identificar riesgos regulatorios y competitivos, y determinar qué iniciativas pueden escalar de un piloto a una solución empresarial. Articula la IA en términos de crecimiento, diferenciación y sostenibilidad.',
    characteristics: [
      'Lenguaje: Habla en términos de "ROI", "ventaja competitiva", "escalabilidad", "alineación estratégica", "gobernanza".',
      'Enfoque: Alinear IA con objetivos de negocio — generar valor estratégico medible y ventajas difíciles de replicar.',
      'Visión del Riesgo: Evalúa riesgos estratégicos: regulatorios, competitivos, de escalabilidad y de desalineación con el negocio.',
      'Medida del Éxito: ROI comprobado, ventaja competitiva sostenible y alineación con la estrategia corporativa.'
    ]
  },
  transformador: {
    number: 5,
    title: 'Transformador',
    description: 'IA como pilar de innovación y cultura: rediseña modelos de negocio y roles organizacionales.',
    fullDescription: 'El Transformador representa el nivel más alto de madurez. Para este profesional, la IA no es una herramienta ni una estrategia: es un pilar fundamental de innovación y cultura organizacional. Ha demostrado capacidad para rediseñar modelos de negocio, crear nuevos flujos de valor y transformar roles dentro de la organización. Lidera el cambio cultural necesario para que toda la organización adopte la IA de manera responsable, gestiona la tensión entre velocidad de innovación y gobernanza ética, y actúa como evangelizador de la transformación. Su impacto trasciende lo individual para ser organizacional y sistémico.',
    characteristics: [
      'Lenguaje: Articula conceptos de "transformación organizacional", "innovación de modelo de negocio", "gobernanza ética", "cambio cultural".',
      'Enfoque: Rediseñar el negocio — crear nuevos modelos de ingresos, transformar roles y construir cultura de IA.',
      'Visión del Riesgo: Gestiona la tensión entre innovación y gobernanza ética, transparencia y responsabilidad a escala organizacional.',
      'Medida del Éxito: Transformación del negocio: nuevos modelos de ingresos, cambio cultural y ventaja competitiva sostenible.'
    ]
  }
};

// calculateDiagnosticResult removed — evaluation is now done via AI edge function
